import { Fragment, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import CheckIcon from "@mui/icons-material/Check";
// import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import CountryFlag from "react-country-flag";
import PublicIcon from "@mui/icons-material/Public"; // Add this import at the top

type Country = {
  name: string;  // Display name (capitalized)
  value: string; // Backend value (lowercase)
  code: string;  // Country code for flag
};

interface CountryDropdownProps {
  selected: Country;
  setSelected: (country: Country) => void;
}

const countryOptions: Country[] = [
  { name: "Select Country", value: "", code: "" },
  { name: "Worldwide", value: "worldwide", code: "WW" },
  { name: "United States", value: "united states", code: "US" },
  { name: "Netherlands", value: "netherlands", code: "NL" },
  { name: "New Zealand", value: "new zealand", code: "NZ" },
  { name: "Norway", value: "norway", code: "NO" },
  { name: "China", value: "china", code: "CN" },
  { name: "Poland", value: "poland", code: "PL" },
  { name: "Portugal", value: "portugal", code: "PT" },
  { name: "Philippines", value: "philippines", code: "PH" },
  { name: "Russia", value: "russia", code: "RU" },
  { name: "Saudi Arabia", value: "saudi arabia", code: "SA" },
  { name: "South Africa", value: "south africa", code: "ZA" },
  { name: "Spain", value: "spain", code: "ES" },
  { name: "Sweden", value: "sweden", code: "SE" },
  { name: "Switzerland", value: "switzerland", code: "CH" },
  { name: "Taiwan", value: "taiwan", code: "TW" },
  { name: "Turkey", value: "turkey", code: "TR" },
  { name: "United Kingdom", value: "united kingdom", code: "GB" },
  { name: "Argentina", value: "argentina", code: "AR" },
  { name: "Australia", value: "australia", code: "AU" },
  { name: "Austria", value: "austria", code: "AT" },
  { name: "Belgium", value: "belgium", code: "BE" },
  { name: "Brazil", value: "brazil", code: "BR" },
  { name: "Canada", value: "canada", code: "CA" },
  { name: "Chile", value: "chile", code: "CL" },
  { name: "Denmark", value: "denmark", code: "DK" },
  { name: "Finland", value: "finland", code: "FI" },
  { name: "France", value: "france", code: "FR" },
  { name: "Germany", value: "germany", code: "DE" },
  { name: "Hong Kong", value: "hong kong", code: "HK" },
  { name: "India", value: "india", code: "IN" },
  { name: "Indonesia", value: "indonesia", code: "ID" },
  { name: "Italy", value: "italy", code: "IT" },
  { name: "Japan", value: "japan", code: "JP" },
  { name: "South Korea", value: "south korea", code: "KR" },
  { name: "Malaysia", value: "malaysia", code: "MY" },
  { name: "Mexico", value: "mexico", code: "MX" },
];

export default function CountryDropdown({ selected, setSelected }: CountryDropdownProps) {
  const [searchQuery, setSearchQuery] = useState("");

  // Filter countries based on search query
  const filteredCountries = countryOptions.filter(country => 
    country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.value.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Listbox value={selected} onChange={setSelected}>
      {({ open }) => (
        <div className={`relative mt-1 ${open ? "z-[200]" : "z-10"}`}>
          <Listbox.Button
            className="relative w-full md:w-[240px] h-14 cursor-pointer flex items-center justify-between text-white rounded-[12px] pl-5 pr-2 py-2 focus:outline-none bg-transparent border border-[#1F1F1F]"
          >
            <span className="flex items-center gap-2">
              {selected.code
                ? (selected.code === "WW"
                    ? <PublicIcon style={{ fontSize: "1.5em", color: "#94a3b8" }} />
                    : <CountryFlag countryCode={selected.code} svg style={{ width: "1.5em", height: "1.5em", borderRadius: "4px" }} />)
                : <PublicIcon style={{ fontSize: "1.5em", color: "#94a3b8" }} />
              }
              <span>{selected.name}</span>
            </span>

            {/* Arrow pill: 56x40, radius 135px, bg #1A1A1A; SVG 12x7.4 white */}
            <span
              className={`pointer-events-none ml-2 inline-flex items-center justify-center rounded-[135px] w-14 h-10 ${open ? "bg-[#232323]" : "bg-[#1A1A1A]"}`}
            >
              <svg width="12" height="7.4" viewBox="0 0 12 8" fill="none" aria-hidden="true">
                <path d="M1 2l5 4 5-4" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </Listbox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute mt-1 w-full md:w-[240px] max-h-60 overflow-auto bg-[#0F0F0F] border border-[#1F1F1F] rounded-[12px] shadow-lg">
              <div className="sticky top-0 z-50 bg-[#0F0F0F] p-2 border-b border-[#1F1F1F]/30">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-transparent text-white border border-[#1F1F1F]/60 rounded-md px-4 py-2 pl-10 focus:outline-none"
                    placeholder="Search country..."
                    onClick={(e) => e.stopPropagation()}
                  />
                  <SearchIcon
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    style={{ fontSize: 20 }}
                  />
                </div>
              </div>

              {filteredCountries.map((country, idx) => (
                <Listbox.Option
                  key={idx}
                  className={({ active }) =>
                    `cursor-pointer select-none relative py-2 pl-4 pr-10 flex items-center gap-2 ${
                      active ? "bg-white/5 text-white" : "text-gray-200"
                    }`
                  }
                  value={country}
                >
                  {({ selected }) => (
                    <>
                      <span className="flex items-center gap-2">
                        {country.code && (
                          country.code === "WW" ? (
                            <PublicIcon style={{ fontSize: "1.5em", color: "#94a3b8" }} />
                          ) : (
                            <CountryFlag
                              countryCode={country.code}
                              svg
                              style={{ width: "1.5em", height: "1.5em", borderRadius: "4px" }}
                            />
                          )
                        )}
                        <span>{country.name}</span>
                      </span>
                      {selected ? (
                        <span className="absolute inset-y-0 right-4 flex items-center text-gray-300">
                          <CheckIcon style={{ fontSize: 22 }} />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
}